import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { usePublicExperience } from '../context/PublicExperienceContext';
import { buildRouteMetadata } from '../utils/appMetadata';

const upsertMetaTag = (attributeName, attributeValue, content) => {
  if (typeof document === 'undefined' || !attributeValue || !content) {
    return;
  }

  let element = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const upsertLinkTag = (rel, href) => {
  if (typeof document === 'undefined' || !rel || !href) {
    return;
  }

  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

function AppMetadata() {
  const location = useLocation();
  const { experience } = usePublicExperience();
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
  const metadata = useMemo(
    () =>
      buildRouteMetadata({
        pathname: location.pathname,
        experience,
        siteUrl,
      }),
    [experience, location.pathname, siteUrl],
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.lang = metadata.locale;
    document.title = metadata.title;

    upsertMetaTag('name', 'description', metadata.description);
    upsertMetaTag('name', 'application-name', metadata.siteName);
    upsertMetaTag('name', 'apple-mobile-web-app-title', metadata.shortName);
    upsertMetaTag('name', 'theme-color', metadata.themeColor);
    upsertMetaTag('name', 'twitter:card', 'summary');
    upsertMetaTag('name', 'twitter:title', metadata.title);
    upsertMetaTag('name', 'twitter:description', metadata.description);
    upsertMetaTag('name', 'twitter:image', metadata.image);
    upsertMetaTag('name', 'twitter:image:alt', metadata.imageAlt);

    upsertMetaTag('property', 'og:site_name', metadata.siteName);
    upsertMetaTag('property', 'og:locale', metadata.locale.replace('-', '_'));
    upsertMetaTag('property', 'og:type', metadata.type);
    upsertMetaTag('property', 'og:title', metadata.title);
    upsertMetaTag('property', 'og:description', metadata.description);
    upsertMetaTag('property', 'og:image', metadata.image);
    upsertMetaTag('property', 'og:image:type', metadata.imageType);
    upsertMetaTag('property', 'og:image:width', metadata.imageWidth);
    upsertMetaTag('property', 'og:image:height', metadata.imageHeight);
    upsertMetaTag('property', 'og:image:alt', metadata.imageAlt);
    upsertMetaTag('property', 'og:url', metadata.url);

    upsertLinkTag('canonical', metadata.canonicalUrl);
  }, [metadata]);

  return null;
}

export default AppMetadata;
